import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles.css";
import DpaModal from "../components/DpaModal";
import { RWG_DPA_TEXT } from "../constants/dpaText";
import { getToken, setAuthHeader, fetchUserDetails, fetchVenueById } from "../utils/api";
import { submitAgreement, fetchExistingAgreement } from "../utils/agreements";

const ReserveWithGooglePage = () => {
  const [searchParams] = useSearchParams();
  // URL params will be used by backend submission later if needed
  // const venueId = searchParams.get('vid');
  // const hash = searchParams.get('hash');
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    restaurantName: "",
    address: ""
  });
  
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [isZonalVenue, setIsZonalVenue] = useState(false);

  // Google Maps will be loaded dynamically
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState(null);
  const successMapRef = useRef(null);
  const successMapInitializedRef = useRef(false);
  const markerRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState(null); // { lat, lng }
  // Note: We no longer display submittedData or apiResponse on the confirmation screen
  const [googlePlaceId, setGooglePlaceId] = useState(null);
  const [clientIp, setClientIp] = useState(null);
  const [showDpa, setShowDpa] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(null); // { acceptedAt }


  // Clear any existing local storage on page load (avoid stale tokens or state)
  useEffect(() => {
    try { localStorage.clear(); } catch (_) {}
  }, []);

  // Fetch client IP
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        if (!cancelled) setClientIp(data.ip);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Authenticate via URL params and fetch user details
  useEffect(() => {

    const uid = searchParams.get("uid") || searchParams.get("userId");
    const urlHash = searchParams.get("hash");

    const init = async () => {
      try {
        if (!uid || !urlHash) {
          setError("Missing credentials in the URL.");
          setInitializing(false);
          return;
        }
        // Always request a fresh token
        const token = await getToken(uid, urlHash);
        setAuthHeader(token);
        const fetchedUser = await fetchUserDetails();

        // Prefill if possible
        setFormData((prev) => ({
          ...prev,
          restaurantName:
            fetchedUser?.company?.legalName || fetchedUser?.company?.name || prev.restaurantName,
        }));

        // Venue lookup by vid and prefill address
        const vid = searchParams.get("vid") || searchParams.get("venueId");
        if (vid) {
          try {
            const venue = await fetchVenueById(vid);
            const venueAddress = venue?.address || venue?.formattedAddress || venue?.location?.formattedAddress;
            setIsZonalVenue(venue?.rms?.name === "Zonal Events");
            if (venueAddress) {
              setFormData((prev) => ({ ...prev, address: venueAddress }));
              updateMapLocation(venueAddress);
            }
            // Check existing agreements for RWG
            try {
              const existing = await fetchExistingAgreement(vid, 'rwg');
              if (existing) {
                setAlreadySigned({ acceptedAt: existing.acceptedAt || existing.createdAt });
                setShowSuccess(true);
                return;
              }
            } catch (_) {}
          } catch (e) {
            // Non-blocking: if venue fetch fails, user can still type address
            console.warn("Venue fetch failed", e);
          }
        }
      } catch (e) {
        setError("Authentication failed. Please check your link.");
      } finally {
        setInitializing(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Geocode and map update helper
  const updateMapLocation = useCallback((address) => {
    const targetMap = map || successMapRef.current;
    if (!targetMap || !address || !window.google || !window.google.maps) return;

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0] && results[0].geometry) {
          const location = results[0].geometry.location;
          if (results[0].place_id) {
            setGooglePlaceId(results[0].place_id);
          }

          if (location && typeof location.lat === 'function' && typeof location.lng === 'function') {
            const lat = location.lat();
            const lng = location.lng();

            if (isFinite(lat) && isFinite(lng)) {
              setSelectedLocation({ lat, lng });
              targetMap.setCenter({ lat, lng });
              targetMap.setZoom(15);

              if (markerRef.current) {
                markerRef.current.setMap(null);
              }

              const newMarker = new window.google.maps.Marker({
                position: { lat, lng },
                map: targetMap,
                title: formData.restaurantName || 'Restaurant Location'
              });
              markerRef.current = newMarker;
            }
          }
        }
      });
    } catch (error) {
      console.error('Error updating map location:', error);
    }
  }, [map, formData.restaurantName]);

  // Removed separate handleAddressSelect (inlined inside initializeAutocomplete)

  useEffect(() => {
    // Load Google Maps API only if not already loaded
    if (!window.google && !document.querySelector('script[src*="maps.googleapis.com"]')) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
        setError('Failed to load Google Maps. Please refresh the page.');
      };
      document.head.appendChild(script);
    } else if (window.google) {
      setMapLoaded(true);
    }
  }, []);

  // Initialize map and places after script load
useEffect(() => {
  if (!initializing && mapLoaded && window.google && map === null) {
      const timer = setTimeout(() => {
        const mapElement = document.getElementById('google-map');
        if (mapElement) {
          const newMap = new window.google.maps.Map(mapElement, {
            center: selectedLocation || { lat: 51.5074, lng: -0.1278 },
            zoom: 15,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          });
          setMap(newMap);
        }
        // Autocomplete
        const addressInput = document.getElementById('address-input');
        if (addressInput && window.google.maps.places) {
          const autocomplete = new window.google.maps.places.Autocomplete(addressInput, {
            types: ['establishment', 'geocode'],
            fields: ['formatted_address', 'geometry', 'name'],
          });
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry && place.formatted_address) {
              setFormData(prev => ({ ...prev, address: place.formatted_address }));
              updateMapLocation(place.formatted_address);
              if (place.place_id) {
                setGooglePlaceId(place.place_id);
              }
            }
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
}, [initializing, mapLoaded, map, updateMapLocation, selectedLocation]);

  // If the address was prefilled before the map initialized, center the map once it's ready
  useEffect(() => {
    if (map && formData.address) {
      updateMapLocation(formData.address);
    }
  }, [map, formData.address, updateMapLocation]);

  // When switching to the success screen, initialize the success map once
  useEffect(() => {
    if (!showSuccess || !mapLoaded || !window.google) return;
    if (successMapInitializedRef.current) return;
    const element = document.getElementById('google-map');
    if (!element) return;

    const center = selectedLocation || { lat: 51.5074, lng: -0.1278 };
    const newMap = new window.google.maps.Map(element, {
      center,
      zoom: 15,
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
    });
    successMapRef.current = newMap;
    successMapInitializedRef.current = true;

    if (markerRef.current) {
      markerRef.current.setMap(null);
    }
    const newMarker = new window.google.maps.Marker({
      position: center,
      map: newMap,
      title: formData.restaurantName || 'Restaurant Location',
    });
    markerRef.current = newMarker;
  }, [showSuccess, mapLoaded, selectedLocation, formData.restaurantName]);

  

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, []);

  // Removed initializeMap and initializeAutocomplete helpers (inlined in effect)

  // (duplicate updateMapLocation removed below)

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update map when address changes
    if (name === 'address' && value) {
      updateMapLocation(value);
    }
  };

  // removed unused handleAddressSelect

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreementAccepted) {
      setError("Please accept the Mozrest Data Processing Agreement to continue.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Collect payload
      const uid = searchParams.get("uid") || searchParams.get("userId");
      const urlHash = searchParams.get("hash");
      const vid = searchParams.get("vid") || searchParams.get("venueId");

      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        restaurantName: formData.restaurantName,
        address: formData.address,
        googlePlaceId: googlePlaceId || null,
        venueId: vid || null,
        userId: uid || null,
        hash: urlHash || null,
        location: selectedLocation || null,
        agreementAcceptedAt: new Date().toISOString(),
      };

      try { sessionStorage.setItem("rwgSubmission", JSON.stringify(payload)); } catch (_) {}

      // Send to API if we have a venueId and userId
      if (payload.venueId && payload.userId) {
        try {
          const partnerNotice = isZonalVenue
            ? 'For this subscription, Zonal charges £25 per active site.'
            : '';
          await submitAgreement({
            venueId: payload.venueId,
            product: 'rwg',
            details: { ...payload, partnerNotice },
            dpaText: RWG_DPA_TEXT,
            clientIp,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          });
        } catch (apiErr) {
          console.warn("Agreement creation failed", apiErr);
        }
      }

      setShowSuccess(true);
    } catch (err) {
      setError("Failed to submit the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // No explicit close button on success screen; we inform the user instead

  // Hard gate: while initializing (auth/venue/agreement checks), render only loading
  if (!showSuccess && initializing) {
    return (
      <div className="rwg-page-container">
        <div className="rwg-content">
          <div className="rwg-card">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading Data Processing Agreement</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="rwg-page-container">
        <div className="rwg-content">
          <div className="rwg-card">
            <h1>Reserve with Google onboarding</h1>
            {isZonalVenue && (
              <p style={{ marginTop: 8 }}>
                For this subscription, Zonal charges £25 per active site.
              </p>
            )}
            {initializing && <div className="loading-container"><p>Loading...</p></div>}
            <div className="rwg-row">
              <div className="rwg-form-section">
                <div className="success-message">
                  {alreadySigned ? (
                    <p>
                      The Reserve with Google agreement was already signed{alreadySigned.acceptedAt ? ` on ${new Date(alreadySigned.acceptedAt).toLocaleString()}` : ""}.
                    </p>
                  ) : (
                    <p>Congratulations, the Reserve with Google service will be activated within the following days.</p>
                  )}
                </div>
                <p className="confirm-footnote" style={{ marginTop: 24, color: "#667085", textAlign: "center" }}>
                  You may now safely close this window. Thank you.
                </p>
              </div>
              <div className="rwg-map-section">
                {!mapLoaded ? (
                  <div className="map-loading">
                    <p>Loading map...</p>
                  </div>
                ) : (
                  <div id="google-map" className="google-map"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rwg-page-container">
      <div className="rwg-content">
        <div className="rwg-card">
          {initializing ? (
            <div className="loading-container"><p>Loading...</p></div>
          ) : (
            <>
              {error && <div className="error-message">{error}</div>}
              <h1>Reserve with Google onboarding</h1>
              {isZonalVenue && (
                <p style={{ marginTop: 8 }}>
                  For this subscription, Zonal charges £25 per active site.
                </p>
              )}
              <div className="rwg-row">
            <div className="rwg-form-section">
              <form onSubmit={handleSubmit} className="rwg-form">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Name of the restaurant (legal name) *</label>
                  <input
                    type="text"
                    name="restaurantName"
                    value={formData.restaurantName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Address of the restaurant *</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="Start typing an address..."
                    id="address-input"
                  />
                </div>

                <div className="agreement-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={agreementAccepted}
                      onChange={(e) => setAgreementAccepted(e.target.checked)}
                    />
                    <span>
                      I confirm I have read and agree to the{" "}
                      <button
                        type="button"
                        className="link-terms agreement-link"
                        onClick={() => setShowDpa(true)}
                      >
                        Mozrest Data Processing Agreement
                      </button>
                      .
                    </span>
                  </label>
                </div>

                <div className="button-container">
                  <button
                    type="submit"
                    className="button"
                    disabled={!agreementAccepted || isSubmitting}
                  >
                    {isSubmitting ? "Processing..." : "Next"}
                  </button>
                </div>
              </form>
            </div>
            <div className="rwg-map-section">
              {!mapLoaded ? (
                <div className="map-loading">
                  <p>Loading map...</p>
                </div>
              ) : (
                <div id="google-map" className="google-map"></div>
              )}
            </div>
              </div>
            </>
          )}
        </div>
      </div>
      {showDpa && <DpaModal onClose={() => setShowDpa(false)} />}
    </div>
  );
};

export default ReserveWithGooglePage;
